import { Route, Routes } from 'react-router-dom';
import { t } from '@lingui/macro';

import { NavigationMenu, PlmMainMenu } from '../side-nav';
import RoutePerformanceLog from '../performance-log/RoutePerformanceLog';
import { Dashboard, DashboardProvider } from '../page-framework';
import PlmAdminPages from '../pages-admin/PlmAdminPages';
import {
    AccessPointDetailPage,
    AccessPointsPage,
    AgentDetailPage,
    AgentsPage,
    AlertsPage,
    BusinessServiceDetailPage,
    BusinessServicesOverviewPage,
    BusinessServicesPage,
    HypervisorDetailPage,
    HypervisorsPage,
    ImpactedBusinessServicesPage,
    ImpactedSitesPage,
    InterfaceDetailPage,
    InterfacesPage,
    NetworkDeviceDetailPage,
    NetworkDevicesPage,
    PathDetailPage,
    RadioDetailPage,
    RadiosPage,
    ReportsPage,
    ServerDetailPage,
    ServersPage,
    SiteDetailPage,
    SitesOverviewPage,
    SitesPage,
    TestDetailPage,
    TestLogPage,
    TestsPage,
    UiPerformancePage,
    VirtualMachineDetailPage,
    VirtualMachinesPage,
    VoipTestNowPage,
    WebTestNowPage,
    NetTestNowPage,
    WirelessControllerDetailPage,
    WirelessControllersPage,
    NoAccessPage,
} from '../pages';
import '../panels';
import '../filters';
import { LastLocation } from '../side-nav/SidebarLocationHistory';
import { addPlmPrefix, usePlmId } from '../../utilities/PgmUtilities';
import LastPageRedirector from './LagePageRedirector';
import { UserPages } from './UserPages';
import { EnvironmentStoreContainer } from '../../stores/EnvironmentStore';
import { useEnvironmentService } from '../../services/EnvironmentService';
import { FilterStateContainer } from '../../stores/FilterStateStore';
import { useDataService } from '../../services/ApiDataService';
import NetTestNowResultsPage from '../pages/net-test-now-page/NetTestNowResultsPage';
import VoipTestNowResultsPage from '../pages/voip-test-now-page/VoipTestNowResultsPage';
import WebTestNowResultsPage from '../pages/web-test-now-page/WebTestNowResultsPage';

export default function PlmPages() {
    const plmId = usePlmId();
    const stateId = !plmId ? undefined : `pgm-default-menu-state-${plmId}`;
    let menuTitle = 'Main menu';

    if (plmId) {
        menuTitle = t`Local`;
    }

    return (
        <EnvironmentStoreContainer environmentService={useEnvironmentService()}>
            <FilterStateContainer name="plm-filter-state" dataService={useDataService()}>
                <LastLocation stateId={stateId}>
                    {(lastLocationUrl) => (
                        <DashboardProvider>
                            <NavigationMenu
                                title={menuTitle}
                                id="main"
                                showTitle={!!plmId}
                                lastUrl={addPlmPrefix(lastLocationUrl, plmId)}
                                render={() => <PlmMainMenu />}
                            >
                                <Routes className="flex-container">
                                    <Route path="*" element={<PlmReportingPages />} />
                                    <Route path="users/*" element={<UserPages />} />
                                    <Route path="admin/*" element={<PlmAdminPages />} />
                                </Routes>
                            </NavigationMenu>
                        </DashboardProvider>
                    )}
                </LastLocation>
            </FilterStateContainer>
        </EnvironmentStoreContainer>
    );
}

function PlmReportingPages() {
    return (
        <Routes>
            <Route path="/" element={<LastPageRedirector />} />

            <RoutePerformanceLog path="access-points" element={AccessPointsPage} />
            <RoutePerformanceLog path="access-point-detail" element={AccessPointDetailPage} />
            <RoutePerformanceLog path="agent-detail" element={AgentDetailPage} />
            <RoutePerformanceLog path="agents" element={AgentsPage} />
            <RoutePerformanceLog path="alerts" element={AlertsPage} />
            <RoutePerformanceLog path="business-service-detail" element={BusinessServiceDetailPage} />
            <RoutePerformanceLog path="business-services" element={BusinessServicesPage} />
            <RoutePerformanceLog path="business-services-overview" element={BusinessServicesOverviewPage} />
            <RoutePerformanceLog path="vmware-hosts" element={HypervisorsPage} />
            <RoutePerformanceLog path="vmware-host-detail" element={HypervisorDetailPage} />
            <RoutePerformanceLog path="problem-business-services" element={ImpactedBusinessServicesPage} />
            <RoutePerformanceLog path="problem-sites" element={ImpactedSitesPage} />
            <RoutePerformanceLog path="interface-detail" element={InterfaceDetailPage} />
            <RoutePerformanceLog path="interfaces" element={InterfacesPage} />
            <RoutePerformanceLog path="network-devices" element={NetworkDevicesPage} />
            <RoutePerformanceLog path="device-detail" element={NetworkDeviceDetailPage} />
            <RoutePerformanceLog path="path-detail" element={PathDetailPage} />
            <RoutePerformanceLog path="reports" element={ReportsPage} />
            <RoutePerformanceLog path="radios" element={RadiosPage} />
            <RoutePerformanceLog path="radio-detail" element={RadioDetailPage} />
            <RoutePerformanceLog path="servers" element={ServersPage} />
            <RoutePerformanceLog path="server-detail" element={ServerDetailPage} />
            <RoutePerformanceLog path="site-detail" element={SiteDetailPage} />
            <RoutePerformanceLog path="sites" element={SitesPage} />
            <RoutePerformanceLog path="sites-overview" element={SitesOverviewPage} />
            <RoutePerformanceLog path="test-detail" element={TestDetailPage} />
            <RoutePerformanceLog path="test-log" element={TestLogPage} />
            <RoutePerformanceLog path="tests" element={TestsPage} />
            <RoutePerformanceLog path="ui-performance" element={UiPerformancePage} />
            <RoutePerformanceLog path="vmware-vms" element={VirtualMachinesPage} />
            <RoutePerformanceLog path="vmware-vm-detail" element={VirtualMachineDetailPage} />
            <RoutePerformanceLog path="quickTests/voip" element={VoipTestNowPage} />
            <RoutePerformanceLog path="quickTests/voip/:id" element={VoipTestNowResultsPage} />
            <RoutePerformanceLog path="quickTests/web" element={WebTestNowPage} />
            <RoutePerformanceLog path="quickTests/web/:id" element={WebTestNowResultsPage} />
            <RoutePerformanceLog path="net-test-now" element={NetTestNowPage} />
            <RoutePerformanceLog path="net-test-now/:id" element={NetTestNowResultsPage} />
            <RoutePerformanceLog path="wireless-controllers" element={WirelessControllersPage} />
            <RoutePerformanceLog path="wireless-controller-detail" element={WirelessControllerDetailPage} />
            <RoutePerformanceLog path="no-access" element={NoAccessPage} />
            <RoutePerformanceLog path=":pageId" element={Dashboard} />
        </Routes>
    );
}
